import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import ListItemButton from "@mui/material/ListItemButton";

import { makeStyles } from "tss-react/mui";

import NotificationsIcon from "@mui/icons-material/Notifications";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";

import Moment from "react-moment";

import { setNotifications } from "../../store/actions";

import NotificationService from "../../service/notificationServices";

const useStyles = makeStyles()((theme) => ({
  notificationMenu: {
    marginTop: "20px",
    paddingTop: 0,
    "& .MuiList-root": {
      padding: 0,
    },
  },
  notificationMenuPaper: {
    height: "50px",
    width: "100%",
    padding: 0,
    display: "flex",
    alignItems: "center",
    borderRadius: "0px",
    position: "sticky",
    top: "0px",
    left: "0px",
    zIndex: "10000",
  },
  noNotificationBox: {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    height: "70px",
    width: "100%",
  },
  listItem: {
    "&:hover": {
      "& .bread-crum-link": {
        textDecoration: "underline",
      },
    },
  },
}));

function NotificationsList() {
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notificationsUpdating, setNotificationsUpdating] = useState(false);

  const notifications = useSelector((state) => state.notifications);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { classes } = useStyles();

  const isNotificationMenuOpen = Boolean(notificationAnchorEl);

  const getNameInitials = (firstName, lastName) => {
    return firstName?.charAt(0) + lastName?.charAt(0);
  };

  const refreshTodayNotifications = useCallback(async () => {
    setNotificationsUpdating(true);
    const notificationsService = new NotificationService();
    const { data, success } = await notificationsService.getNotifications(user.currentUser.User_id, 0);
    if (success) {
      if (data.todayNotifications.length !== notifications.todayNotifications.length) {
        dispatch(
          setNotifications({
            todayNotifications: data.todayNotifications,
            notSeenNotificationCount: data.notSeenNotificationCount,
          })
        );
      }
    }
    setNotificationsUpdating(false);
  }, [dispatch, notifications.todayNotifications.length, user.currentUser.User_id]);

  const handleNotificationMenuOpen = async (event) => {
    setNotificationAnchorEl(event.currentTarget);
    await refreshTodayNotifications();
  };

  const handleRefreshClick = async () => {
    await refreshTodayNotifications();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
    if (notifications.notSeenNotificationCount > 0) {
      const notificationsService = new NotificationService();
      notificationsService.setSeenNotifications(user.currentUser.User_id);
      dispatch(
        setNotifications({
          notSeenNotificationCount: 0,
        })
      );
    }
  };

  const handlePageRedirect = (url) => {
    if (notifications.notSeenNotificationCount > 0) {
      const notificationsService = new NotificationService();
      notificationsService.setSeenNotifications(user.currentUser.User_id);
      dispatch(
        setNotifications({
          notSeenNotificationCount: 0,
        })
      );
    }
    navigate(url);
  };

  useEffect(() => {
    if (notifications.sync) refreshTodayNotifications();

    return () => {};
  }, [notifications, refreshTodayNotifications]);

  const renderNotificationMenu = (
    <Menu
      anchorEl={notificationAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isNotificationMenuOpen}
      onClose={handleNotificationMenuClose}
      slotProps={{
        paper: {
          sx: {
            minWidth: "600px",
            maxWidth: "600px",
            minHeight: "80px",
            maxHeight: "700px",
            marginTop: "10px",
            borderRadius: "16px",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              borderRadius: "10px",
              marginTop: "50px",
              marginBottom: "16px",
            },
          },
        },
      }}
      className={classes.notificationMenu}
    >
      <Paper elevation={2} className={classes.notificationMenuPaper}>
        <Box
          sx={{
            padding: "10px",
            display: "flex",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            alignContent: "center",
          }}
        >
          <NotificationsIcon sx={{ color: "#3a3d4a", width: 24, height: 24, transform: "rotate(30deg)", mr: "10px" }} />
          <Box
            sx={{
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: "bolder",
                width: "100%",
              }}
            >
              Notifications
            </Typography>
          </Box>
          {notificationsUpdating ? (
            <CircularProgress sx={{ height: "24px !important", width: "24px !important" }} />
          ) : (
            <IconButton onClick={handleRefreshClick}>
              <RefreshIcon sx={{ width: 24, height: 24 }} />
            </IconButton>
          )}
        </Box>
      </Paper>
      {Object.keys(notifications).length === 0 ||
      (notifications?.todayNotifications?.length === 0 && notifications?.olderNotifications?.length === 0) ? (
        <Box className={classes.noNotificationBox}>You don't have any notifications</Box>
      ) : (
        <List>
          {notifications?.todayNotifications?.length !== 0 ? (
            <>
              <ListSubheader>Today</ListSubheader>
              {notifications.todayNotifications.map(({ Notification_id, Detail, URL, FormatedDate }, index) => {
                return (
                  <ListItemButton
                    onClick={() => handlePageRedirect(URL)}
                    key={Notification_id}
                    className={classes.listItem}
                    divider
                  >
                    <ListItem alignItems="flex-start" disablePadding>
                      <ListItemAvatar>
                        <Avatar
                          alt={getNameInitials(Detail.PerformedBy?.FirstName, Detail.PerformedBy?.LastName)}
                          sx={{
                            backgroundColor:
                              Detail?.PerformedBy.user_id === user.currentUser.User_id ? "#5243AA" : "#00875A",
                          }}
                        >
                          {getNameInitials(Detail.PerformedBy?.FirstName, Detail.PerformedBy?.LastName)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            noWrap
                          >{`${Detail.PerformedBy.FirstName} ${Detail.PerformedBy.LastName}`}</Typography>
                        }
                        secondary={
                          <>
                            <Typography
                              sx={{ display: "block" }}
                              component="span"
                              variant="body2"
                              color="text.primary"
                              className={"bread-crum-link"}
                            >
                              {`Workspace: ${Detail?.Title}`}
                            </Typography>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                              className={"bread-crum-link"}
                            >
                              {`${Detail.Message}`}
                            </Typography>
                          </>
                        }
                      />
                      <Chip
                        label={<Moment fromNow>{FormatedDate}</Moment>}
                        sx={{
                          mt: "10px",
                        }}
                      />
                    </ListItem>
                  </ListItemButton>
                );
              })}
            </>
          ) : null}
          {notifications?.olderNotifications?.length !== 0 ? (
            <>
              <ListSubheader>Older</ListSubheader>
              {notifications.olderNotifications.map(({ Notification_id, Detail, URL, FormatedDate }, index) => {
                return (
                  <ListItemButton
                    onClick={() => handlePageRedirect(URL)}
                    divider
                    key={Notification_id}
                    className={classes.listItem}
                  >
                    <ListItem alignItems="flex-start" disablePadding>
                      <ListItemAvatar>
                        <Avatar
                          alt={getNameInitials(Detail.PerformedBy?.FirstName, Detail.PerformedBy?.LastName)}
                          sx={{
                            backgroundColor:
                              Detail?.PerformedBy.user_id === user.currentUser.User_id ? "#5243AA" : "#00875A",
                          }}
                        >
                          {getNameInitials(Detail.PerformedBy?.FirstName, Detail.PerformedBy?.LastName)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            noWrap
                          >{`${Detail.PerformedBy.FirstName} ${Detail.PerformedBy.LastName}`}</Typography>
                        }
                        secondary={
                          <>
                            <Typography
                              sx={{ display: "block" }}
                              component="span"
                              variant="body2"
                              color="text.primary"
                              className={"bread-crum-link"}
                            >
                              {`Workspace: ${Detail?.Title}`}
                            </Typography>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                              className={"bread-crum-link"}
                            >
                              {`${Detail.Message}`}
                            </Typography>
                          </>
                        }
                      />
                      <Chip
                        label={<Moment fromNow>{FormatedDate}</Moment>}
                        sx={{
                          mt: "10px",
                        }}
                      />
                    </ListItem>
                  </ListItemButton>
                );
              })}
            </>
          ) : null}
          <Paper
            sx={{
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
              height: "50px",
              width: "100%",
            }}
            elevation={3}
          >
            That's all your notifications from last 30 days.
          </Paper>
        </List>
      )}
    </Menu>
  );

  return (
    <>
      <IconButton
        data-id="notificationsNoneIcon"
        color="inherit"
        size="large"
        edge="start"
        aria-label="Notifications"
        aria-haspopup="true"
        onClick={handleNotificationMenuOpen}
      >
        <Badge
          overlap="circular"
          badgeContent=" "
          variant="dot"
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            transform: "rotate(30deg)",
            "& .MuiBadge-badge": {
              backgroundColor: notifications.notSeenNotificationCount > 0 ? "#d32f2f" : "transparent",
            },
          }}
        >
          <Tooltip title="Notifications" arrow>
            <NotificationsIcon
              sx={{ color: "#3a3d4a", width: 24, height: 24, "&:hover": { color: "#0052CC" } }}
              className="notifcationsList"
            />
          </Tooltip>
        </Badge>
      </IconButton>
      {isNotificationMenuOpen ? renderNotificationMenu : null}
    </>
  );
}

export default React.memo(NotificationsList);
